/**
 * HUD wave strip: exit prep (start horde) or hurry the next spawn on the active wave.
 * AS3: mana + stats + badges from starting waves early (IngameCore + StatId).
 */

import type { RenderPort, AudioPort } from "../ports.js";
import { sessionToView } from "../state-view.js";
import {
  hurryNextMonsterSpawn,
  earlyHurryManaFromSavedAs3,
  addMana,
  recordSessionEarlyHurryReward,
} from "../../domain/session.js";
import type { GameSession } from "../../domain/session.js";

export interface SummonWaveDeps {
  render: RenderPort;
  getSession(): GameSession | null;
  /** Composition wrapper so tutorial hooks run when leaving prep. */
  setSpeed: (speed: 0 | 1 | 3) => void;
  audio?: AudioPort;
  /** Career stats + achievement checks (composition wires mage profile). */
  onEarlyHurryReward?: (mana: number) => void;
}

export function createSummonWave(deps: SummonWaveDeps) {
  return (waveIndex: number): void => {
    const session = deps.getSession();
    if (!session || session.won !== null) return;

    const prepPhase = session.speed === 0 && session.elapsedMs === 0;

    if (prepPhase) {
      if (waveIndex !== session.currentWaveIndex) return;
      deps.setSpeed(1);
      return;
    }

    if (waveIndex !== session.currentWaveIndex) return;

    const hurried = hurryNextMonsterSpawn(session);
    if (!hurried.ok) {
      deps.render.update(sessionToView(session, "game"));
      return;
    }

    const wave = session.waves[session.currentWaveIndex];
    // Light bonus only once per wave: prevents farming by clicking the same
    // wave button repeatedly to hurry one monster at a time.
    if (!wave.earlyHurryRewarded) {
      const mana = earlyHurryManaFromSavedAs3(
        session.currentWaveIndex,
        hurried.savedMs,
        wave.intervalMs
      );
      if (mana > 0) {
        addMana(session, mana);
        recordSessionEarlyHurryReward(session, mana);
        deps.onEarlyHurryReward?.(mana);
        deps.audio?.play("earlyWaveMana");
      }
      wave.earlyHurryRewarded = true;
    }

    deps.render.update(sessionToView(session, "game"));
  };
}
